import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type {
  CurrentActiveDiscountsResponseData,
  FetchDataParams,
  ServicesListParams,
  ServicesListResponseData,
  ServicesUsageHistoryResponseData,
} from '@/types/api/queries';

import { HttpClient } from '@/network/HttpClient';

export const SERVICES_LIST_QUERY_KEY = 'services_list';
export const CURRENT_ACTIVE_DISCOUNTS_QUERY_KEY = 'current_active_discounts';
export const PREVIOUSLY_ACTIVE_DISCOUNTS_QUERY_KEY =
  'previously_active_discounts';
export const CURRENT_PURCHASED_ACTIVE_SERVICES_QUERY_KEY =
  'current_purchased_active_services';
export const CURRENT_PURCHASED_NOT_ACTIVE_SERVICES_QUERY_KEY =
  'current_purchased_not_active_services';
export const PREVIOUSLY_PURCHASED_ACTIVE_SERVICES_QUERY_KEY =
  'previously_purchased_active_services';
export const SERVICE_USAGE_HISTORY_QUERY_KEY = 'service_usage_history';

export const useCurrentActiveDiscountsQuery = ({
  fetchUrl,
  paginationParams,
  filters,
}: FetchDataParams) =>
  useQuery({
    queryKey: [
      CURRENT_ACTIVE_DISCOUNTS_QUERY_KEY,
      fetchUrl,
      paginationParams,
      filters,
    ],
    queryFn: () =>
      HttpClient.request<CurrentActiveDiscountsResponseData>({
        method: 'GET',
        url: fetchUrl,
        params: {
          ...(paginationParams || {}),
          ...(filters && { filters }),
        },
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const usePreviouslyActiveDiscountsQuery = ({
  fetchUrl,
  paginationParams,
  filters,
}: FetchDataParams) =>
  useQuery({
    queryKey: [
      PREVIOUSLY_ACTIVE_DISCOUNTS_QUERY_KEY,
      fetchUrl,
      paginationParams,
      filters,
    ],
    queryFn: () =>
      HttpClient.request<CurrentActiveDiscountsResponseData>({
        method: 'GET',
        url: fetchUrl,
        params: {
          ...(paginationParams || {}),
          ...(filters && { filters }),
        },
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const useCurrentPurchasedActiveServicesQuery = ({
  fetchUrl,
  paginationParams,
  filters,
}: FetchDataParams) =>
  useQuery({
    queryKey: [
      CURRENT_PURCHASED_ACTIVE_SERVICES_QUERY_KEY,
      fetchUrl,
      paginationParams,
      filters,
    ],
    queryFn: () =>
      HttpClient.request<CurrentActiveDiscountsResponseData>({
        method: 'GET',
        url: fetchUrl,
        params: {
          ...(paginationParams || {}),
          ...(filters && { filters }),
        },
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const useCurrentPurchasedNotActiveServicesQuery = ({
  fetchUrl,
  paginationParams,
  filters,
}: FetchDataParams) =>
  useQuery({
    queryKey: [
      CURRENT_PURCHASED_NOT_ACTIVE_SERVICES_QUERY_KEY,
      fetchUrl,
      paginationParams,
      filters,
    ],
    queryFn: () =>
      HttpClient.request<CurrentActiveDiscountsResponseData>({
        method: 'GET',
        url: fetchUrl,
        params: {
          ...(paginationParams || {}),
          ...(filters && { filters }),
        },
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const usePreviouslyPurchasedActiveServicesQuery = ({
  fetchUrl,
  paginationParams,
  filters,
}: FetchDataParams) =>
  useQuery({
    queryKey: [
      PREVIOUSLY_PURCHASED_ACTIVE_SERVICES_QUERY_KEY,
      fetchUrl,
      paginationParams,
      filters,
    ],
    queryFn: () =>
      HttpClient.request<CurrentActiveDiscountsResponseData>({
        method: 'GET',
        url: fetchUrl,
        params: {
          ...(paginationParams || {}),
          ...(filters && { filters }),
        },
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const useServiceUsageHistoryQuery = ({
  fetchUrl,
  paginationParams,
}: FetchDataParams) =>
  useQuery({
    queryKey: [SERVICE_USAGE_HISTORY_QUERY_KEY, fetchUrl, paginationParams],
    queryFn: () =>
      HttpClient.request<ServicesUsageHistoryResponseData>({
        method: 'GET',
        url: fetchUrl,
        params: paginationParams,
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

export const useServicesListQuery = (
  url: string,
  queryParams: ServicesListParams['queryParams'],
) =>
  useQuery({
    queryKey: [SERVICES_LIST_QUERY_KEY, url, queryParams],
    queryFn: () =>
      HttpClient.request<ServicesListResponseData>({
        method: 'GET',
        url,
        params: queryParams,
      }),
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
